import type { ComponentProps, ReactNode } from 'react'
import { ImageCompany } from '@/routes/_app/-components/assets/image-company'
import { cn, getInitials } from '@/lib/utils'
import { getRuntimeImgUrl, sizedImageUrl } from '@/lib/sized-image'
import type { ProfileType } from 'shared'
import { useLingui } from '@lingui/react/macro'
import { cva, type VariantProps } from 'class-variance-authority'
import { PROFILE_TYPE_ICON_SVGS, ProfileTypeIcon } from './profile-type-icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

// For avatar use the expertly-crafted default color palette for tailwind: https://tailwindcss.com/docs/customizing-colors
const personColors = [
  '#ef4444', // Red
  '#f97316', // Orange
  '#f59e0b', // Amber
  '#eab308', // Yellow
  '#84cc16', // Lime
  '#22c55e', // Green
  '#10b981', // Emerald
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#0ea5e9', // Sky
  '#3b82f6', // Blue
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#a855f7', // Purple
  '#d946ef', // Fuchsia
  '#ec4899', // Pink
  '#f43f5e', // Rose
].reverse()

// Background color classes for non-person profile types
// Each type/market gets a distinct amber shade to differentiate them
const profileTypeColorClasses: Record<string, string> = {
  job: 'bg-blue-500/85', // Blue with transparency
  market_job: 'bg-amber-400/90',
  market_applicant: 'bg-amber-400/90',
  market: 'bg-amber-400/90',
  market_company: 'bg-amber-400/90',
  market_company_job: 'bg-amber-400/90',
  company: 'bg-slate-500/85', // Slate with transparency
  team: 'bg-violet-500/85', // Violet with transparency
  org_group: 'bg-emerald-500/85', // Slate with transparency
  public: 'bg-emerald-500/85', // Slate with transparency
}

const variantColorClasses: Record<string, string> = {
  job: 'bg-indigo-500/85',
  org: 'bg-emerald-500/85',
}

// Subtle avatars (list rows, the assistant): a 10% tint of the entity color with its initials or icon in the color,
// set on the avatar so an icon takes it too, going to 30% and a deeper shade under a data-selected=true `Item` (not the
// solid color: the squashed icon-rail stacks a dozen). A 400 shade on the light tint is too faint to read.
// Tailwind requires static class names — keep these literal.
const subtleClassMap: Record<string, { tint: string; ink: string }> = {
  indigo: {
    tint: 'bg-indigo-500/10 group-data-[selected=true]/item:bg-indigo-500/30 transition-colors',
    ink: 'text-indigo-500 group-data-[selected=true]/item:text-indigo-700 dark:group-data-[selected=true]/item:text-indigo-200',
  },
  blue: {
    tint: 'bg-blue-500/10 group-data-[selected=true]/item:bg-blue-500/30 transition-colors',
    ink: 'text-blue-500 group-data-[selected=true]/item:text-blue-700 dark:group-data-[selected=true]/item:text-blue-200',
  },
  violet: {
    tint: 'bg-violet-500/10 group-data-[selected=true]/item:bg-violet-500/30 transition-colors',
    ink: 'text-violet-500 group-data-[selected=true]/item:text-violet-700 dark:group-data-[selected=true]/item:text-violet-200',
  },
  emerald: {
    tint: 'bg-emerald-500/10 group-data-[selected=true]/item:bg-emerald-500/30 transition-colors',
    ink: 'text-emerald-500 group-data-[selected=true]/item:text-emerald-700 dark:group-data-[selected=true]/item:text-emerald-200',
  },
  slate: {
    tint: 'bg-slate-500/10 group-data-[selected=true]/item:bg-slate-500/30 transition-colors',
    ink: 'text-slate-500 group-data-[selected=true]/item:text-slate-700 dark:group-data-[selected=true]/item:text-slate-200',
  },
  amber: {
    tint: 'bg-amber-400/10 group-data-[selected=true]/item:bg-amber-400/30 transition-colors',
    ink: 'text-amber-600 dark:text-amber-400 group-data-[selected=true]/item:text-amber-700 dark:group-data-[selected=true]/item:text-amber-200',
  },
}

const getSubtleColorName = (type?: ProfileType, variant?: string): string => {
  if (variant === 'job') return 'indigo'
  if (variant === 'org') return 'emerald'
  if (type === 'job') return 'blue'
  if (type === 'team') return 'violet'
  if (type === 'org_group' || type === 'public') return 'emerald'
  if (type === 'company') return 'slate'
  if (
    type === 'market_job' ||
    type === 'market_applicant' ||
    type === 'market' ||
    type === 'market_company' ||
    type === 'market_company_job'
  )
    return 'amber'
  return 'slate'
}

const hashString = (string: string) => {
  let hash = 0
  for (let i = 0; i < string.length; ++i) hash = string.charCodeAt(i) + ((hash << 5) - hash)
  return hash
}

const stringToColor = (string: string, isPerson: boolean) => {
  if (!isPerson) return undefined
  const hash = hashString(string)
  return personColors[Math.abs(hash % personColors.length)]
}

const getColorClass = (type?: ProfileType, variant?: string) => {
  if (variant && variant in variantColorClasses) {
    return variantColorClasses[variant]
  }
  if (type && type in profileTypeColorClasses) {
    return profileTypeColorClasses[type]
  }
  return 'bg-slate-500/85'
}

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden items-center justify-center font-medium text-white select-none rounded-md',
  {
    variants: {
      size: {
        'xs': 'size-5 text-xs rounded-full',
        'sm': 'size-6 text-sm rounded-sm',
        'md': 'size-8 text-lg',
        'lg': 'size-9 text-xl',
        'xl': 'size-10 text-2xl',
        '2xl': 'size-11 text-3xl',
        '3xl': 'size-12 text-4xl rounded-lg',
        '4xl': 'size-16 text-4xl rounded-lg',
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
)

// Each size's edge in CSS px, what the image service is asked twice of.
const AVATAR_PX = { 'xs': 20, 'sm': 24, 'md': 32, 'lg': 36, 'xl': 40, '2xl': 44, '3xl': 48, '4xl': 64 } as const

function withTooltip(element: React.ReactElement, tooltip?: ReactNode) {
  if (!tooltip) return element
  return (
    <Tooltip>
      <TooltipTrigger render={element} />
      <TooltipContent side="bottom" className="p-2">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

export function Avatar({
  size = 'lg',
  url,
  type,
  name,
  className,
  variant,
  forceInitials,
  subtle,
  tooltip,
  ...props
}: Omit<ComponentProps<'span'>, 'style'> & {
  size?: VariantProps<typeof avatarVariants>['size']
  url?: string | null
  type?: ProfileType
  name?: string | null
  variant?: 'job' | 'org'
  /** When true, render only initials — skip the type icon and the URL image. */
  forceInitials?: boolean
  /**
   * When true, render with a 10% tint of the entity color at rest, deepening to 30% with darker text
   * when an ancestor `Item` has data-selected=true.
   * Designed for avatars inside CollapsibleListItem rows.
   */
  subtle?: boolean
  tooltip?: ReactNode
}) {
  const { t } = useLingui()
  const isPerson = type === 'applicant' || type === 'member' || type === 'employee' || type === 'org_user'
  const id = `${isPerson ? 'applicant' : type}-${name}`
  const background = stringToColor(id, isPerson)
  const colorClass = !isPerson ? getColorClass(type, variant) : undefined
  const subtleTone = subtle && !isPerson ? subtleClassMap[getSubtleColorName(type, variant)] : undefined
  const initials = getInitials(name ?? '', type ?? '')
  // White on the solid fills; a subtle avatar's ink is set on the avatar itself, image or not.
  const ink = subtleTone ? undefined : 'text-white'

  const getIcon = () => {
    // An applicant still registering has no image and no name yet — show the
    // generic applicant icon instead of empty initials.
    if (type === 'applicant' && !name) return PROFILE_TYPE_ICON_SVGS.applicant
    if (type === 'market_job') return PROFILE_TYPE_ICON_SVGS.market
    if (type === 'market_applicant') return PROFILE_TYPE_ICON_SVGS.market
    if (type === 'company') return null // Uses ImageCompany instead
    if (variant === 'job') return PROFILE_TYPE_ICON_SVGS.offer
    if (type === 'job') return PROFILE_TYPE_ICON_SVGS.job
    if (type === 'team') return PROFILE_TYPE_ICON_SVGS.team
    if (type === 'org_group') return PROFILE_TYPE_ICON_SVGS.team
    if (type === 'market') return PROFILE_TYPE_ICON_SVGS.market
    if (type === 'market_company') return PROFILE_TYPE_ICON_SVGS.market
    if (type === 'market_company_job') return PROFILE_TYPE_ICON_SVGS.market
    return null
  }

  const icon = getIcon()

  // Company logo / entity icon / initials on the colored background: the resting content for image-less
  // avatars, and what shows through until the photo below arrives. `forceInitials` shows only the initials.
  const fallbackContent = forceInitials ? (
    <span className={cn(!isPerson && [ink, 'text-xs font-bold'])}>{initials}</span>
  ) : type === 'company' ? (
    <>
      <ImageCompany
        className={cn(subtleTone ? 'fill-current' : 'fill-white', '@max-[100px]/panel:hidden')}
        width="45%"
        height="45%"
      />
      <span className={cn(ink, 'hidden text-xs font-bold @max-[100px]/panel:block')}>{initials}</span>
    </>
  ) : icon ? (
    <>
      <ProfileTypeIcon icon={icon} className={cn(ink, '@max-[100px]/panel:hidden')} width="45%" height="45%" />
      {/* When collapsed into the icon-rail (ancestor `@container/squash` ≤ 100px), show initials
          instead of the generic entity icon so items are distinguishable. */}
      <span className={cn(ink, 'hidden text-xs font-bold @max-[100px]/panel:block')}>{initials}</span>
    </>
  ) : (
    <span className={cn(!isPerson && [ink, 'font-bold'])}>{initials}</span>
  )

  const hasImage = !forceInitials && !!url
  const photo =
    hasImage && url ? sizedImageUrl(getRuntimeImgUrl(), url, AVATAR_PX[size ?? 'lg'], isPerson ? 'face' : 'logo') : ''
  const tone = subtleTone ? cn(subtleTone.tint, subtleTone.ink) : colorClass
  const toneStyle = background ? { background } : undefined

  return withTooltip(
    <span
      {...props}
      role="img"
      aria-label={t`User avatar`}
      className={cn(avatarVariants({ size }), hasImage ? subtleTone?.ink : tone, className)}
      style={hasImage ? undefined : toneStyle}
    >
      {/* With an image on top, the colour moves to its own layer 1px inside the edge: painted on the rounded box
          itself, it bled through the image's anti-aliased corners as a grey rim. */}
      {hasImage && (
        <span
          aria-hidden
          className={cn('absolute inset-px rounded-[inherit]', subtleTone?.tint ?? colorClass)}
          style={toneStyle}
        />
      )}
      {hasImage ? (
        <span className="relative flex items-center justify-center">{fallbackContent}</span>
      ) : (
        fallbackContent
      )}
      {/* A BACKGROUND, not an <img>: one that 404s or is still in flight paints nothing, so the browser
          alone decides whether the photo or the initials underneath show — first frame of the SSR HTML,
          nothing waiting on hydration. An <img> has to be hidden by React until `onLoad`, because Chromium
          paints its broken-image glyph even with alt="" — and that gate is what issue #76 came out of. */}
      {hasImage && (
        <span
          aria-hidden
          className={cn(
            'absolute inset-0 rounded-[inherit] bg-center bg-no-repeat',
            // A person's photo fills the circle; a logo (company, job, team) is shown whole on a white tile, since
            // `cover` cropped a wide wordmark down to its middle letters.
            isPerson ? 'bg-cover' : 'bg-white bg-contain bg-origin-content p-[12%]',
          )}
          style={{ backgroundImage: `url("${photo.replace(/["\\]/g, encodeURIComponent)}")` }}
        />
      )}
    </span>,
    tooltip,
  )
}
